import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { CurrencyConversionApiService } from '@rd/core/services/currency-conversion-api/currency-conversion-api.service';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import {
  CurrencyConversion,
  IconTheme,
  LoyaltyCurrency,
  LoyaltyProgram,
} from '@rd/types';
import { NUMBER_ONLY_REGEX } from '@rd/utils/regex.utils';
import { EMPTY, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { TransferConnectV2CheckoutFormData } from '../../types';

@Component({
  selector: 'rd-tcv2-checkout-transfer-amount-form',
  templateUrl: './checkout-transfer-amount-form.component.html',
  styleUrls: ['./checkout-transfer-amount-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TcV2CheckoutTransferAmountFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TcV2CheckoutTransferAmountFormComponent),
      multi: true,
    },
  ],
})
export class TcV2CheckoutTransferAmountFormComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @HostBinding('class.rd-tcv2-checkout-transfer-amount-form')
  componentClass = true;
  @Input() collapsibleTransferAmountForm: boolean;
  @Input() currencyConversionLoading: boolean;
  @Input() formData: TransferConnectV2CheckoutFormData;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() isSubmitted: Subject<boolean>;
  @Output()
  updateCurrencyConversionLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  updateDebounceLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  formattedDenomination: string;
  loyaltyCurrencyName: string;
  transferAmountForm: UntypedFormGroup;
  transferIconUrl: string;
  targetCurrency: string;
  isDebounceLoading = false;
  changesFromButton = false;
  maxSourceInputValue = 0;

  private destroy$ = new Subject();

  propagateChange: (value: any) => {};
  onTouched: () => void = () => {};

  constructor(
    protected cdRef: ChangeDetectorRef,
    protected fb: UntypedFormBuilder,
    protected currencyConversionApiService: CurrencyConversionApiService,
    protected loyaltyCurrencyService: LoyaltyCurrencyService,
    protected numberService: NumberService,
    protected urlUtilsService: UrlUtilsService,
    protected iconRegistryService: IconRegistryService,
    protected transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {
    this.setTransferIconUrl();
  }

  private setTransferIconUrl(): void {
    this.transferIconUrl = this.urlUtilsService.imageUrl(
      '/images/rd/icn-transfer.svg',
    );
  }

  ngOnInit(): void {
    (this.transferAmountForm = this.fb.group({
      sourceAmount: [
        null,
        {
          validators: [
            Validators.pattern(NUMBER_ONLY_REGEX),
            Validators.required,
          ],
        },
      ],
      targetAmount: [
        null,
        {
          validators: [Validators.pattern(NUMBER_ONLY_REGEX)],
          updateOn: 'blur',
        },
      ],
    })),
      this.isSubmitted.subscribe((event) => {
        if (event) {
          this.setAmountFormRequiredFields();
          this.transferAmountForm.markAllAsTouched();
          this.cdRef.markForCheck();
        }
      });

    this.transferAmountForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.cdRef.markForCheck();
      });

    this.setupTransferAmountFormListeners();
    this.setInitialAmountFields();
    this.registerIcons();
    // @ts-ignore
    this.targetCurrency = this.loyaltyProgram.loyaltyCurrency.name;

    Promise.resolve().then(() =>
      this.propagateChange(this.transferAmountForm.value),
    );

    this.formattedDenomination = this.numberService.formatNumber(
      // @ts-ignore
      this.loyaltyProgram.transferRate.denomination,
    );

    this.loyaltyCurrencyName =
      // @ts-ignore
      this.loyaltyCurrencyService.loyaltyCurrencySelected[
        this.transferConnectV2SettingService.checkoutTransferAmountFormSourceCurrencyNameAttribute
      ];

    this.maxSourceInputValue =
      Math.floor(
        this.maxInputAmountValue /
          // @ts-ignore
          this.loyaltyProgram.transferRate.denomination,
      ) *
      // @ts-ignore
      this.loyaltyProgram.transferRate.denomination;
  }

  // Prepopulate sourceAmount if formData is populated. Don't prepopulate targetAmount, as the
  // async validator will prepopulate for us.
  private setInitialAmountFields(): void {
    if (this.formData) {
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).setValue(
        this.formData.sourceAmount,
      );
    } else {
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).setValue(
        // @ts-ignore
        this.loyaltyProgram.transferRate.minimumTransfer,
      );
    }
  }

  private setAmountFormRequiredFields(): void {
    const { sourceAmount, targetAmount } = this.transferAmountForm.controls;
    sourceAmount.setValidators([
      Validators.required,
      Validators.pattern(NUMBER_ONLY_REGEX),
    ]);
    targetAmount.setValidators([
      Validators.required,
      Validators.pattern(NUMBER_ONLY_REGEX),
    ]);
    sourceAmount.updateValueAndValidity();
    targetAmount.updateValueAndValidity();
  }

  writeValue(val: any): void {
    if (!!val) {
      this.transferAmountForm.setValue(val, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.transferAmountForm.valueChanges.subscribe(fn);
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled
      ? this.transferAmountForm.disable()
      : this.transferAmountForm.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.transferAmountForm.valid
      ? null
      : { invalidTransferAmountForm: true };
  }

  downwardArrowImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/arrow-l-blue-R.svg');
  }

  minusIconTheme(): IconTheme {
    if (this.atMinimumTransferAmount()) {
      return 'disabled';
    } else {
      return 'link';
    }
  }

  plusIconTheme(): IconTheme {
    if (
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).value >=
      this.maxSourceInputValue
    ) {
      return 'disabled';
    } else {
      return 'link';
    }
  }

  addPoints(): void {
    const sourceAmountField = this.transferAmountForm.get(
      'sourceAmount',
    ) as AbstractControl;

    if (sourceAmountField.value >= this.maxSourceInputValue) {
      return;
    }

    if (sourceAmountField.valid) {
      this.changesFromButton = true;
      this.emitUpdateDebounceLoading(true);
      sourceAmountField.setValue(
        Number(sourceAmountField.value) +
          // @ts-ignore
          this.loyaltyProgram.transferRate.denomination,
      );
    }
  }

  subtractPoints(): void {
    if (this.atMinimumTransferAmount()) {
      return;
    }

    const sourceAmountField = this.transferAmountForm.get(
      'sourceAmount',
    ) as AbstractControl;

    if (sourceAmountField.valid) {
      this.changesFromButton = true;
      this.emitUpdateDebounceLoading(true);
      sourceAmountField.setValue(
        Number(sourceAmountField.value) -
          // @ts-ignore
          this.loyaltyProgram.transferRate.denomination,
      );
    }
  }

  private atMinimumTransferAmount(): boolean {
    const sourceAmountField = this.transferAmountForm.get(
      'sourceAmount',
    ) as AbstractControl;
    return (
      sourceAmountField.value ===
      // @ts-ignore
      this.loyaltyProgram.transferRate.minimumTransfer
    );
  }

  /*
  ====================================
    Form Error Checkers
  ====================================
  */

  isSourceAmountEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).hasError(
        'required',
      ),
    );
  }

  isSourceAmountNotNumber(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).hasError(
        'pattern',
      ),
    );
  }

  isTargetAmountEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('targetAmount') as AbstractControl).hasError(
        'required',
      ),
    );
  }

  isTargetAmountNotNumber(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('targetAmount') as AbstractControl).hasError(
        'pattern',
      ),
    );
  }

  // Must ensure form is touched already, as specs fail unless this check is present
  private validateAfterFormIsTouched(validateFn: () => boolean): boolean {
    return this.transferAmountForm.touched && validateFn();
  }

  /*
  ====================================
    Translations/Rendering Helpers
  ====================================
  */

  targetDenomination(): number {
    return (
      // @ts-ignore
      this.loyaltyProgram.transferRate.denomination *
      // @ts-ignore
      this.loyaltyProgram.transferRate.exchangeRate
    );
  }

  /*
    ====================================
  */

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('icn-transfer');
    this.iconRegistryService.registerIcon('plus');
    this.iconRegistryService.registerIcon('minus');
  }

  private setupTransferAmountFormListeners(): void {
    const sourceAmountField = this.transferAmountForm.get(
      'sourceAmount',
    ) as AbstractControl;

    sourceAmountField.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(1000),
        filter((inputValue) => !!inputValue && sourceAmountField.valid),
        distinctUntilChanged(),
        tap(() => {
          this.emitUpdateCurrencyConversionLoading(true);
        }),
        switchMap((sourceAmount: number) => {
          return this.currencyConversionApiService
            .postCurrencyConversion({
              // @ts-ignore
              homeCurrencyId: this.sourceCurrency().id,
              // @ts-ignore
              targetCurrencyId:
                this.loyaltyProgram.transferRate.loyaltyCurrencyId,
              redemptionAmount:
                sourceAmount > this.maxSourceInputValue
                  ? this.maxSourceInputValue
                  : sourceAmount,
            })
            .pipe(
              catchError(() => {
                this.emitUpdateCurrencyConversionLoading(false);
                return EMPTY;
              }),
            );
        }),
      )
      .subscribe((currencyConversion) =>
        this.applyCurrencyConversionValues(currencyConversion),
      );

    const targetAmountField = this.transferAmountForm.get(
      'targetAmount',
    ) as AbstractControl;
    targetAmountField.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter(
          () => targetAmountField.valid && targetAmountField.value != null,
        ),
        switchMap((targetAmount: number) => {
          this.emitUpdateCurrencyConversionLoading(true);
          return this.currencyConversionApiService
            .postCurrencyConversion({
              // @ts-ignore
              homeCurrencyId: this.sourceCurrency().id,
              // @ts-ignore
              targetCurrencyId:
                this.loyaltyProgram.transferRate.loyaltyCurrencyId,
              transferAmount: targetAmount,
            })
            .pipe(
              catchError(() => {
                this.emitUpdateCurrencyConversionLoading(false);
                return EMPTY;
              }),
            );
        }),
      )
      .subscribe((currencyConversion) =>
        this.applyCurrencyConversionValues(currencyConversion),
      );
  }

  private applyCurrencyConversionValues(
    currencyConversion: CurrencyConversion,
  ): void {
    if (
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).value >
      this.maxSourceInputValue
    ) {
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).setValue(
        this.maxSourceInputValue,
        {
          emitEvent: false,
        },
      );
    } else {
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).setValue(
        currencyConversion.redemptionAmount,
        {
          emitEvent: false,
        },
      );
    }

    (this.transferAmountForm.get('targetAmount') as AbstractControl).setValue(
      currencyConversion.transferAmountBase,
      {
        emitEvent: false,
      },
    );

    this.propagateChange(this.transferAmountForm.value);

    this.emitUpdateCurrencyConversionLoading(false);
    this.emitUpdateDebounceLoading(false);
    this.cdRef.markForCheck();
  }

  emitUpdateCurrencyConversionLoading(loading: boolean): void {
    this.updateCurrencyConversionLoading.emit(loading);
  }

  emitUpdateDebounceLoading(loading: boolean): void {
    this.isDebounceLoading = loading;
    this.updateDebounceLoading.emit(loading);
  }

  iconActiveClass(): string | undefined {
    if (
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).value >
      // @ts-ignore
      this.loyaltyProgram.transferRate.minimumTransfer
    ) {
      return 'icon-active';
    }
    return;
  }

  get maxInputAmountValue(): number {
    return this.loyaltyProgram.transferRate.maximumTransfer ?? 10_000_000;
  }

  private sourceCurrency(): LoyaltyCurrency | null {
    return this.loyaltyCurrencyService.loyaltyCurrencySelected;
  }
}
